/**
 * VisitsPage — Termin-Übersicht
 *
 * - Mantine-Tabelle: Kunde, geplantes Datum, Status (Badge), Erinnerungs-Ampel, Teilnehmer
 * - Filter: Status-Checkbox, Ampel-Filter (zeige nur rote/orange)
 * - Sortierung: Standard nach Erinnerungs-Ampel (rot zuerst), dann Datum
 * - Button "Termin anlegen" → öffnet Modal
 */
import {
  Anchor,
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Table,
  Tabs,
  Text,
  Tooltip,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { createLink, useSearch } from "@tanstack/react-router";

import { OnlyMineSwitch } from "../components/OnlyMineSwitch.js";
import { parseOnlyMine, useOnlyMine } from "../lib/onlyMine.js";
import { useState, forwardRef, type ComponentPropsWithoutRef } from "react";
import { useTranslation } from "react-i18next";
import { QuadrantBadge } from "../components/QuadrantBadge.js";
import { StatusBadge } from "../components/StatusBadge.js";
import type { Quadrant, VisitExemption } from "@msc/shared";
import { CompleteVisitModal } from "../components/visits/CompleteVisitModal.js";
import { CreateVisitModal } from "../components/visits/CreateVisitModal.js";
import { ExemptionModal } from "../components/visits/ExemptionModal.js";
import { ExemptionRowActions } from "../components/visits/ExemptionRowActions.js";
import { PostponeVisitModal } from "../components/visits/PostponeVisitModal.js";
import { useCampaigns } from "../hooks/useCampaigns.js";
import { useEmployees } from "../hooks/useEmployees.js";
import { api } from "../lib/api.js";
import {
  useVisitReminders,
  useVisits,
  visitKeys,
  type ReminderColor,
  type Visit,
  type VisitStatus,
} from "../hooks/useVisits.js";

// ── Search-Param Validation ──────────────────────────────────────────────────

export interface VisitsSearch {
  filter?: ReminderColor;
  onlyMine?: boolean;
}

export function validateVisitsSearch(raw: Record<string, unknown>): VisitsSearch {
  const filter = raw.filter as string | undefined;
  const onlyMine = parseOnlyMine(raw.onlyMine);
  return {
    filter:
      filter === "red" || filter === "orange" || filter === "green" ? filter : undefined,
    ...(onlyMine !== undefined ? { onlyMine } : {}),
  };
}

// Kundenname als Link in die Kundenakte — dasselbe Muster wie in wartung.tsx:
// `createLink` direkt auf Mantines `Anchor` verliert dessen Props im Typ,
// der forwardRef-Wrapper hält `size` typsicher.
const RouterAnchor = createLink(
  forwardRef<HTMLAnchorElement, ComponentPropsWithoutRef<"a"> & { size?: string }>(
    function RouterAnchorBase(props, ref) {
      return <Anchor ref={ref} {...props} />;
    },
  ),
);

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "–";
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Sortiergewicht: rot zuerst (0), orange (1), grün (2) */
const REMINDER_SORT_ORDER: Record<ReminderColor | "exempt", number> = {
  red: 0,
  orange: 1,
  green: 2,
  exempt: 3,
};

/** Filter im Erinnerungs-Reiter: die Ampelfarben plus „Ausnahme“. */
type ReminderFilter = ReminderColor | "exempt";

interface ReminderBadgeProps {
  color: ReminderColor | "exempt" | undefined;
}

function ReminderBadge({ color }: ReminderBadgeProps) {
  const { t } = useTranslation();
  if (!color) return <Badge color="gray" variant="light">{t("common.dashed")}</Badge>;

  if (color === "exempt") {
    return (
      <Badge color="gray" variant="light" size="sm">
        {t("pages.visits.reminderBadgeExempt")}
      </Badge>
    );
  }

  const labels: Record<ReminderColor, string> = {
    red: t("pages.visits.reminderBadgeRed"),
    orange: t("pages.visits.reminderBadgeOrange"),
    green: t("pages.visits.reminderBadgeGreen"),
  };

  return (
    <Badge color={color} variant="filled" size="sm">
      {labels[color]}
    </Badge>
  );
}

// ── Visits Page ──────────────────────────────────────────────────────────────

const ALL_STATUSES: VisitStatus[] = ["planned", "done", "postponed", "cancelled"];

export function VisitsPage() {
  const { t } = useTranslation();
  const search = useSearch({ strict: false }) as VisitsSearch;
  const initialColorFilter = search.filter ?? null;

  // Filter-State
  const [statusFilter, setStatusFilter] = useState<VisitStatus[]>(["planned", "done"]);
  const [exemptionTarget, setExemptionTarget] = useState<{
    customerId: string;
    customerName: string;
    segmentId: string;
    segmentName: string;
    /** Gültige Ausnahme, die verlängert wird; sonst null. */
    current: VisitExemption | null;
  } | null>(null);
  const [colorFilter, setColorFilter] = useState<ReminderFilter | null>(initialColorFilter);
  const [activeTab, setActiveTab] = useState<"termine" | "reminders">(
    initialColorFilter ? "reminders" : "termine",
  );

  // Modal-State
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [postponeVisit, setPostponeVisit] = useState<Visit | null>(null);
  const [completeVisit, setCompleteVisit] = useState<Visit | null>(null);
  const [cancelVisit, setCancelVisit] = useState<Visit | null>(null);
  const [cancelPending, setCancelPending] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Termin-aus-Erinnerung anlegen: Kunde + Segment vorbelegen
  const [reminderPrefill, setReminderPrefill] = useState<{
    customerId: string;
    customerName: string;
    segmentId: string;
  } | null>(null);

  function openCreateFromReminder(item: {
    customerId: string;
    customerName: string;
    segmentId: string;
  }) {
    setReminderPrefill(item);
    openCreate();
  }

  function handleCreateClose() {
    closeCreate();
    setReminderPrefill(null);
  }

  const queryClient = useQueryClient();
  const { data: employees } = useEmployees();
  const employeeOptions = (employees ?? []).map((e) => ({
    value: e.id,
    label: e.fullName,
  }));
  const { data: activeCampaigns } = useCampaigns({ status: "active" });
  const campaignOptions = (activeCampaigns ?? []).map((c) => ({
    value: c.id,
    label: c.title,
  }));

  async function confirmCancel() {
    if (!cancelVisit) return;
    setCancelPending(true);
    setCancelError(null);
    try {
      await api.patch(`/visits/${cancelVisit.id}`, { status: "cancelled" });
      void queryClient.invalidateQueries({ queryKey: visitKeys.all });
      setCancelVisit(null);
    } catch (err) {
      setCancelError(
        err instanceof Error ? err.message : t("pages.visits.cancelError"),
      );
    } finally {
      setCancelPending(false);
    }
  }

  // Daten
  const { onlyMine } = useOnlyMine();
  const { data, isLoading, isError } = useVisits({
    status: statusFilter.length > 0 ? statusFilter : undefined,
    page: 1,
    onlyMine,
  });

  const { data: remindersData } = useVisitReminders(onlyMine);

  // Reminder-Map: (customerId, segmentId) → ReminderColor (T11 — pro Paar).
  const reminderMap = new Map<string, ReminderColor | "none" | "exempt">();
  for (const r of remindersData?.items ?? []) {
    reminderMap.set(`${r.customerId}|${r.segmentId}`, r.status);
  }

  function reminderColorFor(v: Visit): ReminderColor | undefined {
    const c = reminderMap.get(`${v.customerId}|${v.segmentId}`);
    return c === "red" || c === "orange" || c === "green" ? c : undefined;
  }

  // Sortierung + Ampel-Filter anwenden
  const filteredAndSorted = (data?.items ?? [])
    .filter((v) => {
      if (!colorFilter) return true;
      return reminderColorFor(v) === colorFilter;
    })
    .sort((a, b) => {
      const colorA = reminderColorFor(a);
      const colorB = reminderColorFor(b);
      const sortA = colorA !== undefined ? REMINDER_SORT_ORDER[colorA] : 99;
      const sortB = colorB !== undefined ? REMINDER_SORT_ORDER[colorB] : 99;
      if (sortA !== sortB) return sortA - sortB;
      // Secondary: Datum aufsteigend
      return new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime();
    });

  function toggleStatus(status: VisitStatus) {
    setStatusFilter((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  }

  function toggleColorFilter(color: ReminderFilter) {
    setColorFilter((prev) => (prev === color ? null : color));
    setActiveTab("reminders");
  }

  // Reminder-Liste fürs Erinnerungs-Tab: alle Items mit status red/orange/green,
  // optional auf ausgewählte Ampel-Farbe gefiltert.
  const reminderItems = (remindersData?.items ?? [])
    .filter(
      (r) =>
        r.status === "red" || r.status === "orange" || r.status === "green" || r.status === "exempt",
    )
    .filter((r) => (colorFilter ? r.status === colorFilter : true))
    .sort((a, b) => {
      const sortA = REMINDER_SORT_ORDER[a.status as ReminderFilter] ?? 99;
      const sortB = REMINDER_SORT_ORDER[b.status as ReminderFilter] ?? 99;
      if (sortA !== sortB) return sortA - sortB;
      return a.customerName.localeCompare(b.customerName, "de");
    });

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>{t("pages.visits.title")}</Title>
        <Button onClick={openCreate}>{t("pages.visits.createButton")}</Button>
      </Group>

      {/* Ampel-Zusammenfassung — und daneben die Sicht, damit man hier
          weiterschalten kann, ohne zurück zum Dashboard zu müssen. */}
      {remindersData && (
        <Group gap="sm">
          <OnlyMineSwitch />
          <Text size="sm" c="dimmed">
            {t("pages.visits.filterLabel")}
          </Text>
          {(["red", "orange", "green", "exempt"] as ReminderFilter[]).map((c) => {
            const counts: Record<ReminderFilter, number> = {
              red: remindersData.red,
              orange: remindersData.orange,
              green: remindersData.green,
              exempt: remindersData.exempt ?? 0,
            };
            return (
              <Tooltip
                key={c}
                label={
                  c === "red"
                    ? t("pages.visits.filterRedTooltip")
                    : c === "orange"
                    ? t("pages.visits.filterOrangeTooltip")
                    : c === "green"
                    ? t("pages.visits.filterGreenTooltip")
                    : t("pages.visits.filterExemptTooltip")
                }
              >
                <Badge
                  color={c === "exempt" ? "gray" : c}
                  variant={colorFilter === c ? "filled" : "light"}
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleColorFilter(c)}
                >
                  {counts[c]}
                </Badge>
              </Tooltip>
            );
          })}
          {colorFilter && (
            <Button
              variant="subtle"
              size="xs"
              color="gray"
              onClick={() => setColorFilter(null)}
            >
              {t("pages.visits.filterReset")}
            </Button>
          )}
        </Group>
      )}

      <Tabs
        value={activeTab}
        onChange={(v) => v && setActiveTab(v as "termine" | "reminders")}
      >
        <Tabs.List>
          <Tabs.Tab value="termine">{t("pages.visits.tabTermine")}</Tabs.Tab>
          <Tabs.Tab
            value="reminders"
            rightSection={
              remindersData && (remindersData.red + remindersData.orange) > 0 ? (
                <Badge color="red" size="xs" variant="filled">
                  {remindersData.red + remindersData.orange}
                </Badge>
              ) : undefined
            }
          >
            {t("pages.visits.tabReminders")}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="termine" pt="md">
          <Stack gap="md">
            {/* Status-Filter */}
            <Paper p="sm" withBorder>
              <Group gap="md">
                <Text size="sm" fw={500}>
                  {t("pages.visits.statusLabel")}
                </Text>
                {ALL_STATUSES.map((s) => (
                  <Checkbox
                    key={s}
                    label={
                      s === "planned"
                        ? t("pages.visits.statusPlanned")
                        : s === "done"
                        ? t("pages.visits.statusDone")
                        : s === "postponed"
                        ? t("pages.visits.statusPostponed")
                        : t("pages.visits.statusCancelled")
                    }
                    checked={statusFilter.includes(s)}
                    onChange={() => toggleStatus(s)}
                  />
                ))}
              </Group>
            </Paper>

            {/* Fehler */}
            {isError && (
              <Alert color="red" title={t("common.error")}>
                {t("pages.visits.errorLoading")}
              </Alert>
            )}

            {/* Tabelle — miw verhindert gestauchte Spalten (Badge-Trunkierung
                „ÜBERFÄLL…"); bei schmalem Viewport scrollt die Box. */}
            <Box style={{ overflowX: "auto" }}>
        <Table highlightOnHover striped withTableBorder withColumnBorders miw={860}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("pages.visits.tableCustomer")}</Table.Th>
              <Table.Th>{t("pages.visits.tableSegment")}</Table.Th>
              <Table.Th>{t("pages.visits.tableDate")}</Table.Th>
              <Table.Th>{t("pages.visits.tableStatus")}</Table.Th>
              <Table.Th>{t("pages.visits.tableReminder")}</Table.Th>
              <Table.Th>{t("pages.visits.tableAttendees")}</Table.Th>
              <Table.Th style={{ width: 100 }}>{t("pages.visits.tableActions")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <Table.Tr key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <Table.Td key={j}>
                        <Skeleton height={18} radius="sm" />
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))
              : filteredAndSorted.map((visit) => {
                  const reminderColor = reminderColorFor(visit);
                  return (
                    <Table.Tr key={visit.id}>
                      <Table.Td>
                        <RouterAnchor
                          to="/customers/$id"
                          params={{ id: visit.customerId }}
                          size="sm"
                        >
                          {visit.customerName}
                        </RouterAnchor>
                      </Table.Td>
                      <Table.Td>
                        {visit.segmentName ? (
                          <Badge color="blue" variant="light" size="sm">
                            {visit.segmentName}
                          </Badge>
                        ) : (
                          <Text size="xs" c="dimmed">{t("common.dashed")}</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{formatDate(visit.scheduledAt)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <StatusBadge status={visit.status} />
                      </Table.Td>
                      <Table.Td>
                        <ReminderBadge color={reminderColor} />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={visit.attendees.length === 0 ? "dimmed" : undefined}>
                          {visit.attendees.length > 0
                            ? visit.attendees.map((a) => a.fullName).join(", ")
                            : t("common.dashed")}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          {visit.status === "planned" && (
                            <>
                              <Tooltip label={t("pages.visits.actionComplete")}>
                                <ActionIcon
                                  variant="subtle"
                                  color="green"
                                  size="sm"
                                  onClick={() => setCompleteVisit(visit)}
                                  aria-label={t("pages.visits.actionComplete")}
                                >
                                  <Text size="xs">✓</Text>
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label={t("pages.visits.actionPostpone")}>
                                <ActionIcon
                                  variant="subtle"
                                  color="orange"
                                  size="sm"
                                  onClick={() => setPostponeVisit(visit)}
                                  aria-label={t("pages.visits.actionPostpone")}
                                >
                                  <Text size="xs">⟳</Text>
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label={t("pages.visits.actionCancel")}>
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  size="sm"
                                  onClick={() => setCancelVisit(visit)}
                                  aria-label={t("pages.visits.actionCancel")}
                                >
                                  <Text size="xs">✕</Text>
                                </ActionIcon>
                              </Tooltip>
                            </>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
            {!isLoading && filteredAndSorted.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text c="dimmed" ta="center" py="md">
                    {t("pages.visits.emptyVisits")}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Box>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="reminders" pt="md">
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {t("pages.visits.reminders.description")}
            </Text>
            <Box style={{ overflowX: "auto" }}>
              <Table highlightOnHover striped withTableBorder withColumnBorders miw={960}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("pages.visits.tableCustomer")}</Table.Th>
                    <Table.Th>{t("pages.visits.tableSegment")}</Table.Th>
                    <Table.Th>{t("pages.visits.reminders.tableQuadrant")}</Table.Th>
                    <Table.Th>{t("pages.visits.reminders.tableFrequency")}</Table.Th>
                    <Table.Th>{t("pages.visits.reminders.tableLastVisit")}</Table.Th>
                    <Table.Th>{t("pages.visits.reminders.tableNextVisit")}</Table.Th>
                    <Table.Th>{t("pages.visits.reminders.tableStatus")}</Table.Th>
                    <Table.Th style={{ width: 160 }}>{t("pages.visits.reminders.tableAction")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {reminderItems.map((r) => (
                    <Table.Tr key={`${r.customerId}|${r.segmentId}`}>
                      <Table.Td>
                        <RouterAnchor
                          to="/customers/$id"
                          params={{ id: r.customerId }}
                          size="sm"
                        >
                          {r.customerName}
                        </RouterAnchor>
                      </Table.Td>
                      <Table.Td>
                        <Badge color="blue" variant="light" size="sm">
                          {r.segmentName}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <QuadrantBadge quadrant={(r.quadrant as Quadrant) ?? null} />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {r.frequencyMonths !== null
                            ? t("pages.visits.reminders.frequencyMonths", { count: r.frequencyMonths })
                            : t("common.dashed")}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={r.lastVisitDate ? undefined : "dimmed"}>
                          {r.lastVisitDate ? formatDate(r.lastVisitDate) : t("pages.visits.reminders.noLastVisit")}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={r.nextPlannedDate ? undefined : "dimmed"}>
                          {r.nextPlannedDate ? formatDate(r.nextPlannedDate) : t("common.dashed")}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <ReminderBadge color={r.status as ReminderFilter} />
                        {r.hint === "laeuft_ab" && (
                          <Text size="xs" c="dimmed">
                            {t("pages.visits.exemption.expiringHint")}
                          </Text>
                        )}
                        {r.hint === "verlaengerung_beantragt" && (
                          <Text size="xs" c="dimmed">
                            {t("pages.visits.exemption.renewalRequestedHint")}
                          </Text>
                        )}
                        {r.hint === "beantragt" && (
                          <Text size="xs" c="dimmed">
                            {t("pages.visits.exemption.requestedHint")}
                          </Text>
                        )}
                        {r.exemption?.status === "approved" && (
                          <Text size="xs" c="dimmed">
                            {t("pages.visits.exemption.summary", {
                              until: formatDate(r.exemption.validUntil),
                              reason: t(`pages.visits.exemption.reasons.${r.exemption.reason}`),
                              requestedBy: r.exemption.requestedBy.fullName,
                              decidedBy: r.exemption.decidedBy?.fullName ?? t("common.dashed"),
                            })}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() =>
                            openCreateFromReminder({
                              customerId: r.customerId,
                              customerName: r.customerName,
                              segmentId: r.segmentId,
                            })
                          }
                        >
                          {t("pages.visits.reminders.createVisitButton")}
                        </Button>
                        <ExemptionRowActions
                          item={r}
                          onRequest={() =>
                            setExemptionTarget({
                              customerId: r.customerId,
                              customerName: r.customerName,
                              segmentId: r.segmentId,
                              segmentName: r.segmentName,
                              current: r.exemption?.status === "approved" ? r.exemption : null,
                            })
                          }
                        />
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {reminderItems.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={8}>
                        <Text c="dimmed" ta="center" py="md">
                          {colorFilter
                            ? t("pages.visits.reminders.emptyWithFilter", { filter: colorFilter })
                            : t("pages.visits.reminders.empty")}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Box>
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {exemptionTarget && (
        <ExemptionModal
          opened
          onClose={() => setExemptionTarget(null)}
          customerId={exemptionTarget.customerId}
          customerName={exemptionTarget.customerName}
          segmentId={exemptionTarget.segmentId}
          segmentName={exemptionTarget.segmentName}
          current={exemptionTarget.current}
        />
      )}

      {/* Create Modal */}
      <CreateVisitModal
        opened={createOpened}
        onClose={handleCreateClose}
        employees={employeeOptions}
        campaigns={campaignOptions}
        prefilledCustomerId={reminderPrefill?.customerId}
        prefilledCustomerName={reminderPrefill?.customerName}
        prefilledSegmentId={reminderPrefill?.segmentId}
      />

      {/* Postpone Modal */}
      {postponeVisit && (
        <PostponeVisitModal
          opened={Boolean(postponeVisit)}
          onClose={() => setPostponeVisit(null)}
          visitId={postponeVisit.id}
          originalScheduledAt={postponeVisit.scheduledAt}
          customerName={postponeVisit.customerName}
        />
      )}

      {/* Complete Modal */}
      {completeVisit && (
        <CompleteVisitModal
          opened={Boolean(completeVisit)}
          onClose={() => setCompleteVisit(null)}
          visitId={completeVisit.id}
          scheduledAt={completeVisit.scheduledAt}
          customerName={completeVisit.customerName}
        />
      )}

      {/* Cancel Confirmation */}
      <Modal
        opened={Boolean(cancelVisit)}
        onClose={() => {
          if (cancelPending) return;
          setCancelVisit(null);
          setCancelError(null);
        }}
        title={t("pages.visits.cancelTitle")}
        size="sm"
        centered
        closeOnClickOutside={false}
      >
        <Stack gap="md">
          {cancelVisit && (
            <Text size="sm">
              {t("pages.visits.cancelConfirm", {
                customer: cancelVisit.customerName,
                date: formatDate(cancelVisit.scheduledAt),
              })}
            </Text>
          )}
          {cancelError && (
            <Alert color="red" title={t("common.error")}>
              {cancelError}
            </Alert>
          )}
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => {
                setCancelVisit(null);
                setCancelError(null);
              }}
              disabled={cancelPending}
            >
              {t("common.cancel")}
            </Button>
            <Button color="red" loading={cancelPending} onClick={() => void confirmCancel()}>
              {t("pages.visits.cancelTitle")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
